import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { ColumnTypeSelect } from "./column-type-select";
import { Plus, Trash2, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";

interface ColumnDef {
  id: string;
  name: string;
  type: string;
  nullable: boolean;
  defaultValue: string;
  isPrimaryKey: boolean;
  isUnique: boolean;
  isIdentity: boolean;
  identityGeneration: string;
  isGenerated: boolean;
  generatedExpression: string;
}

interface ForeignKeyDef {
  id: string;
  column: string;
  refSchema: string;
  refTable: string;
  refColumn: string;
  onUpdate: string;
  onDelete: string;
}

const FK_ACTIONS = ["NO ACTION", "CASCADE", "SET NULL", "SET DEFAULT", "RESTRICT"];

function newColumn(): ColumnDef {
  return {
    id: crypto.randomUUID(),
    name: "",
    type: "text",
    nullable: true,
    defaultValue: "",
    isPrimaryKey: false,
    isUnique: false,
    isIdentity: false,
    identityGeneration: "ALWAYS",
    isGenerated: false,
    generatedExpression: "",
  };
}

function defaultIdColumn(): ColumnDef {
  return {
    id: crypto.randomUUID(),
    name: "id",
    type: "uuid",
    nullable: false,
    defaultValue: "gen_random_uuid()",
    isPrimaryKey: true,
    isUnique: false,
    isIdentity: false,
    identityGeneration: "ALWAYS",
    isGenerated: false,
    generatedExpression: "",
  };
}

function newForeignKey(schema: string): ForeignKeyDef {
  return {
    id: crypto.randomUUID(),
    column: "",
    refSchema: schema,
    refTable: "",
    refColumn: "",
    onUpdate: "NO ACTION",
    onDelete: "NO ACTION",
  };
}

export function CreateTableDialog({
  open,
  onOpenChange,
  schema,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  onSuccess: () => void;
}) {
  const [name, setName] = useState("");
  const [columns, setColumns] = useState<ColumnDef[]>([defaultIdColumn(), newColumn()]);
  const [enableRls, setEnableRls] = useState(false);
  const [enums, setEnums] = useState<Array<{ name: string }>>([]);

  useEffect(() => {
    if (open) {
      fetch(`/api/database/enums?schema=${encodeURIComponent(schema)}`)
        .then((r) => r.json())
        .then((d) => setEnums(d.enums || []))
        .catch(() => { toast.error("Failed to load enums"); });
    }
  }, [open, schema]);
  const [foreignKeys, setForeignKeys] = useState<ForeignKeyDef[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addColumn() {
    setColumns((prev) => [...prev, newColumn()]);
  }

  function removeColumn(id: string) {
    setColumns((prev) => prev.filter((c) => c.id !== id));
  }

  function updateColumn(id: string, field: keyof ColumnDef, value: unknown) {
    setColumns((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const updated = { ...c, [field]: value };
        if (field === "isPrimaryKey" && value === true) {
          updated.nullable = false;
        }
        return updated;
      })
    );
  }

  function addForeignKey() {
    setForeignKeys((prev) => [...prev, newForeignKey(schema)]);
  }

  function removeForeignKey(id: string) {
    setForeignKeys((prev) => prev.filter((fk) => fk.id !== id));
  }

  function updateForeignKey(id: string, field: keyof ForeignKeyDef, value: string) {
    setForeignKeys((prev) =>
      prev.map((fk) => (fk.id === id ? { ...fk, [field]: value } : fk))
    );
  }

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("Table name is required");
      return;
    }
    if (columns.length === 0) {
      setError("At least one column is required");
      return;
    }
    const emptyCol = columns.find((c) => !c.name.trim() || !c.type);
    if (emptyCol) {
      setError("All columns must have a name and type");
      return;
    }
    for (const fk of foreignKeys) {
      if (!fk.column || !fk.refTable || !fk.refColumn) {
        setError("All foreign keys must have column, referenced table, and referenced column");
        return;
      }
    }

    setSaving(true);
    try {
      const res = await fetch("/api/database/manage-table", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schema,
          name: name.trim().toLowerCase(),
          columns: columns.map((c) => ({
            name: c.name.trim().toLowerCase(),
            type: c.type,
            nullable: c.nullable,
            defaultValue: c.defaultValue || undefined,
            isPrimaryKey: c.isPrimaryKey,
            isUnique: c.isUnique,
            isIdentity: c.isIdentity || undefined,
            identityGeneration: c.isIdentity ? c.identityGeneration : undefined,
            isGenerated: c.isGenerated || undefined,
            generatedExpression: c.isGenerated ? c.generatedExpression : undefined,
          })),
          enableRls,
          foreignKeys: foreignKeys.map((fk) => ({
            column: fk.column,
            refSchema: fk.refSchema,
            refTable: fk.refTable,
            refColumn: fk.refColumn,
            onUpdate: fk.onUpdate,
            onDelete: fk.onDelete,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to create table");
        return;
      }
      onSuccess();
      onOpenChange(false);
      toast.success(`Table "${name}" created`);
      setName("");
      setColumns([defaultIdColumn(), newColumn()]);
      setEnableRls(false);
      setForeignKeys([]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-3xl flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle>Create Table</SheetTitle>
          <SheetDescription>
            Add a new table to the <span className="font-mono text-foreground">{schema}</span> schema.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Table name */}
          <div className="space-y-1.5">
            <Label htmlFor="table-name" className="text-sm">Table Name</Label>
            <Input
              id="table-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my_table"
              className="h-8 text-sm font-mono"
            />
          </div>

          {/* Columns */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Columns</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-sm gap-1"
                onClick={addColumn}
              >
                <Plus className="h-3 w-3" />
                Add Column
              </Button>
            </div>

            <div className="border border-border rounded-md overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[1fr_140px_60px_60px_60px_60px_60px_32px] gap-2 px-3 py-2 bg-secondary/50 text-sm font-medium text-muted-foreground uppercase tracking-wider">
                <span>Name</span>
                <span>Type</span>
                <span className="text-center">PK</span>
                <span className="text-center">Null</span>
                <span className="text-center">Uniq</span>
                <span className="text-center">ID</span>
                <span className="text-center">Gen</span>
                <span></span>
              </div>

              {/* Rows */}
              {columns.map((col) => (
                <div key={col.id} className="border-t border-border">
                  <div className="grid grid-cols-[1fr_140px_60px_60px_60px_60px_60px_32px] gap-2 px-3 py-1.5 items-center">
                    <Input
                      value={col.name}
                      onChange={(e) => updateColumn(col.id, "name", e.target.value)}
                      placeholder="column_name"
                      className="h-7 text-sm font-mono"
                    />
                    <ColumnTypeSelect
                      value={col.type}
                      onChange={(v) => updateColumn(col.id, "type", v)}
                      className="h-7 text-sm"
                      enums={enums}
                    />
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={col.isPrimaryKey}
                        onChange={(e) => updateColumn(col.id, "isPrimaryKey", e.target.checked)}
                        className="accent-primary"
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={col.nullable}
                        onChange={(e) => updateColumn(col.id, "nullable", e.target.checked)}
                        disabled={col.isPrimaryKey}
                        className="accent-primary"
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={col.isUnique}
                        onChange={(e) => updateColumn(col.id, "isUnique", e.target.checked)}
                        className="accent-primary"
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={col.isIdentity}
                        onChange={(e) => updateColumn(col.id, "isIdentity", e.target.checked)}
                        disabled={col.isGenerated}
                        className="accent-primary"
                        title="Identity column"
                      />
                    </div>
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        checked={col.isGenerated}
                        onChange={(e) => {
                          updateColumn(col.id, "isGenerated", e.target.checked);
                          if (e.target.checked) {
                            updateColumn(col.id, "isIdentity", false);
                            updateColumn(col.id, "defaultValue", "");
                          }
                        }}
                        className="accent-primary"
                        title="Generated (computed) column"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeColumn(col.id)}
                      disabled={columns.length <= 1}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  {col.isIdentity && (
                    <div className="px-3 pb-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground shrink-0">Generation:</Label>
                        <select
                          value={col.identityGeneration}
                          onChange={(e) => updateColumn(col.id, "identityGeneration", e.target.value)}
                          className="h-6 rounded border border-input bg-background px-1.5 text-xs"
                        >
                          <option value="ALWAYS">ALWAYS</option>
                          <option value="BY DEFAULT">BY DEFAULT</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {col.isGenerated && (
                    <div className="px-3 pb-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground shrink-0">Expression:</Label>
                        <Input
                          value={col.generatedExpression}
                          onChange={(e) => updateColumn(col.id, "generatedExpression", e.target.value)}
                          placeholder="e.g. first_name || ' ' || last_name"
                          className="h-6 text-xs font-mono flex-1"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5 ml-[78px]">GENERATED ALWAYS AS (...) STORED</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Foreign Keys */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm flex items-center gap-1.5">
                <Link2 className="h-3 w-3" />
                Foreign Keys
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-sm gap-1"
                onClick={addForeignKey}
              >
                <Plus className="h-3 w-3" />
                Add FK
              </Button>
            </div>

            {foreignKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground">No foreign keys defined.</p>
            ) : (
              <div className="space-y-2">
                {foreignKeys.map((fk) => (
                  <div key={fk.id} className="rounded-md border border-border bg-card p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <span className="text-sm font-medium text-muted-foreground">Foreign Key</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeForeignKey(fk.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Column</Label>
                        <Input
                          value={fk.column}
                          onChange={(e) => updateForeignKey(fk.id, "column", e.target.value)}
                          placeholder="user_id"
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Ref Schema</Label>
                        <Input
                          value={fk.refSchema}
                          onChange={(e) => updateForeignKey(fk.id, "refSchema", e.target.value)}
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Ref Table</Label>
                        <Input
                          value={fk.refTable}
                          onChange={(e) => updateForeignKey(fk.id, "refTable", e.target.value)}
                          placeholder="users"
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Ref Column</Label>
                        <Input
                          value={fk.refColumn}
                          onChange={(e) => updateForeignKey(fk.id, "refColumn", e.target.value)}
                          placeholder="id"
                          className="h-7 text-sm font-mono"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">ON UPDATE</Label>
                        <select
                          value={fk.onUpdate}
                          onChange={(e) => updateForeignKey(fk.id, "onUpdate", e.target.value)}
                          className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                        >
                          {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">ON DELETE</Label>
                        <select
                          value={fk.onDelete}
                          onChange={(e) => updateForeignKey(fk.id, "onDelete", e.target.value)}
                          className="h-7 w-full rounded border border-input bg-background px-2 text-sm"
                        >
                          {FK_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Enable RLS */}
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
            <div>
              <p className="text-sm font-medium">Enable Row Level Security</p>
              <p className="text-sm text-muted-foreground">Restrict access with policies after creation</p>
            </div>
            <Switch checked={enableRls} onCheckedChange={setEnableRls} />
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Table
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
